import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';
import { 
  StarIcon,
  MapPinIcon,
  ClockIcon,
  AcademicCapIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  CalendarDaysIcon,
  PlayIcon,
  ChevronLeftIcon,
  HeartIcon,
  ShareIcon,
  CheckCircleIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/solid';
import { HeartIcon as HeartOutline } from '@heroicons/react/24/outline';

interface Teacher {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  instruments: string[];
  specialties?: string[];
  rating: number;
  totalReviews: number;
  hourlyRate: number;
  location: string;
  distance?: string;
  profilePicture?: string;
  bio: string;
  experience: string;
  availability?: string[];
  lessonFormats?: string[];
}

interface StudentPreferences {
  instrument: string;
  genres: string[];
  skillLevel: string;
  lessonLocation: string[];
  age: string;
  learnerType: 'learner' | 'guardian';
  learnerFirstName?: string;
}

interface Review {
  id: string;
  studentName: string;
  rating: number;
  date: string;
  comment: string;
  instrument: string;
  verified: boolean;
}

interface AvailabilitySlot {
  day: string;
  times: string[];
}

// Default values for missing data
const DEFAULT_LESSON_FORMATS = ['Online', 'On-site (teacher\'s studio)', 'In-home (student\'s home)'];
const DEFAULT_SPECIALTIES = ['General Music Instruction'];

// Mock data for teacher reviews
const mockReviews: Review[] = [
  {
    id: '1',
    studentName: 'Emma S.',
    rating: 5,
    date: '2 weeks ago',
    comment: 'Amazing teacher! Sarah has helped me improve my piano technique significantly. Her teaching style is patient and encouraging.',
    instrument: 'Piano',
    verified: true
  },
  {
    id: '2',
    studentName: 'Marcus T.',
    rating: 5,
    date: '1 month ago',
    comment: 'Great jazz instructor. Really knows how to break down complex concepts into manageable pieces.',
    instrument: 'Piano',
    verified: true
  },
  {
    id: '3',
    studentName: 'Lisa R.',
    rating: 4,
    date: '1 month ago',
    comment: 'Very professional and knowledgeable. My daughter loves her lessons with Sarah.',
    instrument: 'Voice',
    verified: true
  }
];

// Mock availability data
const mockAvailability: AvailabilitySlot[] = [
  { day: 'Monday', times: ['2:00 PM', '3:00 PM', '4:00 PM', '7:00 PM'] },
  { day: 'Tuesday', times: ['3:00 PM', '4:00 PM', '5:00 PM'] },
  { day: 'Wednesday', times: ['2:00 PM', '6:00 PM', '7:00 PM'] },
  { day: 'Thursday', times: ['3:00 PM', '4:00 PM', '5:00 PM', '7:00 PM'] },
  { day: 'Friday', times: ['2:00 PM', '3:00 PM'] },
  { day: 'Saturday', times: ['10:00 AM', '11:00 AM', '1:00 PM', '2:00 PM'] },
  { day: 'Sunday', times: ['1:00 PM', '2:00 PM', '3:00 PM'] }
];

const TeacherDetailPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const { teacher, studentPreferences } = location.state || {};
  const [isFavorited, setIsFavorited] = useState(false);
  const [selectedTab, setSelectedTab] = useState('overview');
  const [showAllReviews, setShowAllReviews] = useState(false);

  // Log teacher detail page access
  console.log('📚 Teacher detail page loaded:', teacher);
  console.log('📚 Student preferences:', studentPreferences);

  if (!teacher) {
    console.warn('❌ No teacher data found, redirecting to browse teachers');
    navigate('/browse-teachers');
    return null;
  }

  // Ensure teacher has required properties with fallbacks
  const teacherData = {
    ...teacher,
    name: teacher.name || `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim() || 'Unknown Teacher',
    hourlyRate: teacher.hourlyRate || 50,
    lessonFormats: teacher.lessonFormats && teacher.lessonFormats.length > 0 ? teacher.lessonFormats : DEFAULT_LESSON_FORMATS,
    specialties: teacher.specialties && teacher.specialties.length > 0 ? teacher.specialties : DEFAULT_SPECIALTIES,
    rating: teacher.rating || 0,
    totalReviews: teacher.totalReviews || 0,
    instruments: teacher.instruments || [],
    experience: teacher.experience || 'Professional Music Instructor',
    bio: teacher.bio || 'Dedicated music instructor with passion for teaching.',
    distance: teacher.distance || teacher.location || 'Location available upon booking'
  };

  const isGuardian = studentPreferences?.learnerType === 'guardian';
  const learnerName = studentPreferences?.learnerFirstName || 'you';

  const handleBookLesson = () => {
    console.log('Initiating booking for teacher:', teacherData.name);
    // TODO: Navigate to booking page
    navigate('/book-lesson', { 
      state: { 
        teacher: teacherData,
        studentPreferences 
      } 
    });
  };

  const handleContactTeacher = () => {
    console.log('Opening contact modal for teacher:', teacherData.name);
    // TODO: Open messaging modal or navigate to messaging page
    alert('Contact teacher feature coming soon!');
  };

  const handleToggleFavorite = () => {
    setIsFavorited(!isFavorited);
    console.log(`${isFavorited ? 'Removed from' : 'Added to'} favorites:`, teacherData.name);
  };

  const handleShare = () => {
    console.log('Sharing teacher profile:', teacherData.name);
    // TODO: Implement sharing functionality
    if (navigator.share) {
      navigator.share({
        title: `${teacherData.name} - Music Teacher`,
        text: `Check out ${teacherData.name}, a ${teacherData.instruments.join(' & ')} teacher on LessonLynx`,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  const renderStars = (rating: number, size: 'sm' | 'md' | 'lg' = 'md') => {
    const sizeClasses = {
      sm: 'w-3 h-3',
      md: 'w-4 h-4', 
      lg: 'w-5 h-5'
    };
    
    return (
      <div className="flex items-center space-x-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <StarIcon
            key={star}
            className={`${sizeClasses[size]} ${star <= rating ? 'text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'reviews', label: `Reviews (${teacherData.totalReviews})` },
    { id: 'availability', label: 'Availability' },
    { id: 'pricing', label: 'Pricing' }
  ];

  return (
    <MinimalLayout noPadding>
      <div className="min-h-screen bg-gray-50">
        
        {/* Header with Back Button */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate('/browse-teachers', { state: { studentPreferences } })}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
              >
                <ChevronLeftIcon className="w-5 h-5" />
                <span>Back to Teachers</span>
              </button>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleToggleFavorite}
                  className="p-2 rounded-full hover:bg-gray-100"
                >
                  {isFavorited ? (
                    <HeartIcon className="w-6 h-6 text-red-500" />
                  ) : (
                    <HeartOutline className="w-6 h-6 text-gray-400" />
                  )}
                </button>
                <button
                  onClick={handleShare}
                  className="p-2 rounded-full hover:bg-gray-100"
                >
                  <ShareIcon className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          
          {/* Teacher Profile Header */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-8">
            <div className="p-8">
              <div className="flex flex-col md:flex-row items-start space-y-6 md:space-y-0 md:space-x-8">
                
                {/* Profile Picture */}
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 bg-gray-200 rounded-full flex items-center justify-center">
                    <UserIcon className="w-16 h-16 text-gray-500" />
                  </div>
                  
                  {/* Verification Badge */}
                  <div className="flex items-center justify-center mt-3">
                    <CheckCircleIcon className="w-5 h-5 text-green-500 mr-1" />
                    <span className="text-sm text-green-600 font-medium">Verified Teacher</span>
                  </div>
                </div>

                {/* Teacher Info */}
                <div className="flex-1">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                    <div className="mb-4 md:mb-0">
                      <h1 className="text-3xl font-bold text-gray-900 mb-2">{teacherData.name}</h1>
                      
                                             {/* Instruments */}
                       <div className="flex flex-wrap gap-2 mb-3">
                         {teacherData.instruments.map((instrument: string, index: number) => (
                          <span
                            key={index}
                            className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {instrument}
                          </span>
                        ))}
                      </div>

                      {/* Rating */}
                      <div className="flex items-center space-x-3 mb-3">
                        {renderStars(teacherData.rating, 'lg')}
                        <span className="text-lg font-semibold">{teacherData.rating}</span>
                        <span className="text-gray-600">({teacherData.totalReviews} reviews)</span>
                      </div>

                      {/* Location & Experience */}
                      <div className="flex flex-wrap items-center gap-4 text-gray-600">
                        <div className="flex items-center space-x-1">
                          <MapPinIcon className="w-4 h-4" />
                          <span>{teacherData.distance}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <AcademicCapIcon className="w-4 h-4" />
                          <span>{teacherData.experience}</span>
                        </div>
                                                 <div className="flex items-center space-x-1">
                           <CurrencyDollarIcon className="w-4 h-4" />
                           <span>${(teacherData.hourlyRate / 2).toFixed(2)}/30-min lesson</span>
                         </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col space-y-3 md:ml-6">
                      <button
                        onClick={handleBookLesson}
                        className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
                      >
                        <CalendarDaysIcon className="w-5 h-5" />
                        <span>Book a Lesson</span>
                      </button>
                      <button
                        onClick={handleContactTeacher}
                        className="border border-gray-300 text-gray-700 hover:text-gray-800 hover:border-gray-400 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center space-x-2"
                      >
                        <ChatBubbleLeftRightIcon className="w-5 h-5" />
                        <span>Contact Teacher</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="border-b">
              <div className="flex space-x-8 px-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      selectedTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-8">
              {selectedTab === 'overview' && (
                <div className="space-y-8">
                  {/* Bio */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">About {teacherData.name}</h3>
                    <p className="text-gray-700 leading-relaxed">{teacherData.bio}</p>
                  </div>

                  {/* Specialties */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Specialties</h3>
                                         <div className="flex flex-wrap gap-3">
                       {teacherData.specialties?.map((specialty: string, index: number) => (
                        <span
                          key={index}
                          className="bg-green-100 text-green-800 px-4 py-2 rounded-lg text-sm font-medium"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Lesson Formats */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Lesson Formats</h3>
                                         <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                       {teacherData.lessonFormats?.map((format: string, index: number) => (
                        <div key={index} className="bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center space-x-2">
                            <CheckCircleIcon className="w-5 h-5 text-green-500" />
                            <span className="font-medium text-gray-900">{format}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Teaching Approach */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Teaching Approach</h3>
                    <div className="bg-blue-50 p-6 rounded-lg">
                      <p className="text-gray-700">
                        I believe in creating a supportive and encouraging environment where students can explore their musical interests while building strong technical foundations. My lessons are tailored to each student's individual goals, learning style, and pace.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {selectedTab === 'reviews' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900">Student Reviews</h3>
                    <div className="text-sm text-gray-600">
                      {teacherData.totalReviews} total reviews
                    </div>
                  </div>

                  <div className="space-y-6">
                    {mockReviews.slice(0, showAllReviews ? mockReviews.length : 3).map((review) => (
                      <div key={review.id} className="border-b border-gray-200 pb-6 last:border-b-0">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                              <UserIcon className="w-5 h-5 text-gray-500" />
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-gray-900">{review.studentName}</span>
                                {review.verified && (
                                  <CheckCircleIcon className="w-4 h-4 text-green-500" />
                                )}
                              </div>
                              <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <span>{review.instrument}</span>
                                <span>•</span>
                                <span>{review.date}</span>
                              </div>
                            </div>
                          </div>
                          {renderStars(review.rating)}
                        </div>
                        <p className="text-gray-700">{review.comment}</p>
                      </div>
                    ))}
                  </div>

                  {mockReviews.length > 3 && (
                    <div className="text-center mt-6">
                      <button
                        onClick={() => setShowAllReviews(!showAllReviews)}
                        className="text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {showAllReviews ? 'Show Less' : `Show All ${teacherData.totalReviews} Reviews`}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {selectedTab === 'availability' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">Weekly Availability</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {mockAvailability.map((slot) => (
                      <div key={slot.day} className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-gray-900 mb-3">{slot.day}</h4>
                        <div className="space-y-2">
                          {slot.times.map((time, index) => (
                            <div
                              key={index}
                              className="bg-white px-3 py-2 rounded border text-sm text-gray-700"
                            >
                              {time}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 text-center">
                    <button
                      onClick={handleBookLesson}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
                    >
                      Book a Specific Time
                    </button>
                  </div>
                </div>
              )}

              {selectedTab === 'pricing' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-6">Lesson Pricing</h3>
                                     <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                     {teacherData.lessonFormats?.map((format: string, index: number) => (
                                             <div key={index} className="bg-gray-50 p-6 rounded-lg">
                         <h4 className="font-semibold text-gray-900 mb-3">{format}</h4>
                         <div className="text-3xl font-bold text-blue-600 mb-2">
                           ${(teacherData.hourlyRate / 2).toFixed(2)}
                         </div>
                         <div className="text-sm text-gray-600 mb-4">per 30-minute lesson</div>
                        <ul className="space-y-2 text-sm text-gray-700">
                          <li className="flex items-center space-x-2">
                            <CheckCircleIcon className="w-4 h-4 text-green-500" />
                            <span>Customized lesson plans</span>
                          </li>
                          <li className="flex items-center space-x-2">
                            <CheckCircleIcon className="w-4 h-4 text-green-500" />
                            <span>Progress tracking</span>
                          </li>
                          <li className="flex items-center space-x-2">
                            <CheckCircleIcon className="w-4 h-4 text-green-500" />
                            <span>Practice materials included</span>
                          </li>
                        </ul>
                      </div>
                    ))}
                  </div>
                  <div className="mt-8 text-center">
                    <button
                      onClick={handleBookLesson}
                      className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
                    >
                      Book Your First Lesson
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* FAQ Section */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden mt-8">
            <div className="p-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">Frequently Asked Questions</h3>
              
              <div className="space-y-8">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">What education and/or training do you have that relates to your work?</h4>
                  <p className="text-gray-700 leading-relaxed">
                    I hold a Bachelor of Music in Piano Performance from the University of Music and a Master's degree in Music Education. 
                    I'm also certified in Suzuki Method for both piano and voice, and regularly attend workshops and masterclasses to stay 
                    current with teaching methodologies. I've completed additional training in jazz improvisation and contemporary vocal techniques.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">How did you get started teaching?</h4>
                  <p className="text-gray-700 leading-relaxed">
                    I began teaching while completing my music degree, starting with a few neighborhood kids who wanted to learn piano. 
                    What started as a way to support myself through college quickly became my passion. I discovered I loved helping students 
                    discover their musical abilities and watching their confidence grow. After graduation, I established my private studio and 
                    have been teaching full-time for over 15 years.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">What types of students have you worked with?</h4>
                  <p className="text-gray-700 leading-relaxed">
                    I've taught students from age 4 to 85! My students range from complete beginners who've never touched a piano to advanced 
                    players preparing for college auditions. I work with children, teens, adults returning to music after years away, and seniors 
                    exploring music for the first time. I've also worked with students with learning differences and have experience adapting my 
                    teaching methods to meet diverse learning styles and needs.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">What advice would you give a student looking to hire a teacher in your area of expertise?</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Look for a teacher whose personality and teaching style match your learning preferences. Don't just focus on credentials—while 
                    important, the ability to communicate effectively and create a supportive environment is crucial. Ask about their experience 
                    with students at your level and with your musical interests. A good teacher should be able to explain complex concepts in simple 
                    terms and should genuinely care about your progress and enjoyment of music.
                  </p>
                </div>

                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-3">What questions should students think through before talking to teachers about their needs?</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Consider your goals: Are you learning for fun, to perform, or for academic purposes? Think about your schedule and how much 
                    time you can realistically commit to practice. Consider your learning style—do you prefer structured lessons or more flexible 
                    approaches? Also think about your musical interests and whether you want to focus on classical, popular music, or a mix. 
                    Finally, consider practical matters like lesson location, frequency, and your budget for both lessons and materials.
                  </p>
                </div>
              </div>

              <div className="mt-10 text-center">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Have more questions?</h4>
                  <p className="text-gray-700 mb-4">
                    I'm happy to discuss your specific needs and answer any other questions you might have.
                  </p>
                  <button
                    onClick={() => {
                      console.log('Opening contact modal for FAQ questions');
                      alert('Contact teacher feature coming soon!');
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                  >
                    Ask a Question
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MinimalLayout>
  );
};

export default TeacherDetailPage; 